import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { APP_ENV } from "@/config";

type ErrorFallbackProps = {
    error?: unknown;
};

export default function ErrorFallback({ error }: ErrorFallbackProps) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-rose-900 to-slate-800 flex items-center justify-center relative overflow-hidden">
            {/* Background blobs */}
            <div className="absolute top-24 right-24 w-72 h-72 bg-rose-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-24 left-24 w-56 h-56 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute inset-1/2 -translate-x-1/2 -translate-y-1/2 w-[28rem] h-[28rem] bg-rose-600/5 rounded-full blur-3xl" />

            <div className="z-10 px-4 w-full">
                {/* Logo */}
                <div className="flex justify-center mb-14">
                    <img
                        src="/logos/ironpipeline.svg"
                        alt="IronPipeline"
                        width={280}
                        className="opacity-90"
                    />
                </div>

                {/* Card */}
                <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-10 max-w-md mx-auto text-center shadow-xl">
                    {/* Icon */}
                    <div className="flex justify-center mb-6">
                        <div className="bg-rose-500/10 p-6 rounded-full border border-rose-500/20">
                            <AlertTriangle className="w-14 h-14 text-rose-500" />
                        </div>
                    </div>

                    {/* Title */}
                    <h2 className="text-6xl font-bold text-white mb-4 tracking-tight">
                        Oops
                    </h2>

                    <h3 className="text-lg font-semibold text-white mb-3">
                        Something went wrong
                    </h3>

                    <p className="text-gray-400 text-sm leading-relaxed mb-8"> An unexpected error occurred. Our team has been notified and is already looking into it.</p>

                    {/* Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button
                            onClick={window.location.reload}
                            className="inline-flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 text-sm"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Reload page
                        </button>

                        <Link
                            to="/"
                            className="inline-flex items-center justify-center bg-slate-700/60 hover:bg-slate-700 text-white font-medium px-6 py-3 rounded-lg transition-colors duration-200 text-sm"
                        >
                            Go to Home
                        </Link>
                    </div>

                    {/* Optional debug (QA only) */}
                    {APP_ENV !== "production" && error != null ? (
                        <div className="mt-6 text-left bg-slate-900/60 border border-slate-700 rounded-lg p-4 text-xs text-rose-300 overflow-auto max-h-40">
                            <pre>{error instanceof Error ? error.message : String(error)}</pre>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
